import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  QuestionMarkCircleIcon,
  XMarkIcon,
  SparklesIcon,
  ArrowTopRightOnSquareIcon,
} from '@heroicons/react/24/outline';
import { endpoints, urlBuilders } from '../config/endpoints';

const HelpWidget: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const [answer, setAnswer] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const token = localStorage.getItem('authToken');
  const userType = localStorage.getItem('userType');

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 50);
  }, [open]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const ask = async () => {
    const q = input.trim();
    if (!q || loading) return;
    setLoading(true);
    setAnswer(null);
    setError(null);
    try {
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const res = await fetch(urlBuilders.main(endpoints.main.help.ask), {
        method: 'POST',
        headers,
        body: JSON.stringify({ question: q, user_type: userType }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setAnswer(data.answer as string);
    } catch {
      setError('Could not get an answer. Try again or visit the full help page.');
    } finally {
      setLoading(false);
    }
  };

  const clear = () => { setAnswer(null); setInput(''); setError(null); };

  return (
    <>
      <button
        onClick={() => setOpen(o => !o)}
        className="fixed bottom-6 right-6 z-50 w-12 h-12 rounded-full bg-primary-600 hover:bg-primary-500 text-white shadow-lg flex items-center justify-center transition-colors"
        title={open ? 'Close help' : 'Help'}
        aria-label={open ? 'Close help' : 'Open help'}
      >
        {open
          ? <XMarkIcon className="w-5 h-5" />
          : <QuestionMarkCircleIcon className="w-6 h-6" />}
      </button>

      {open && (
        <div className="fixed bottom-20 right-6 z-50 w-[26rem] rounded-xl border border-dark-border bg-dark-card shadow-2xl flex flex-col">
          <div className="flex items-center justify-between px-4 py-3 border-b border-dark-border flex-shrink-0">
            <div className="flex items-center gap-2">
              <SparklesIcon className="w-4 h-4 text-primary-400" />
              <span className="text-sm font-semibold text-white">Help</span>
            </div>
            <Link
              to="/help"
              onClick={() => setOpen(false)}
              className="text-xs text-gray-400 hover:text-gray-200 flex items-center gap-1 transition-colors"
            >
              Browse docs
              <ArrowTopRightOnSquareIcon className="w-3 h-3" />
            </Link>
          </div>

          <div className="p-4 flex flex-col gap-3">
            <div className="flex gap-2">
              <textarea
                ref={inputRef}
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); ask(); }
                }}
                placeholder="Ask anything about CoachSight..."
                rows={2}
                className="flex-1 resize-none rounded-lg bg-dark-hover border border-dark-border text-sm text-gray-200 placeholder-gray-500 px-3 py-2 focus:outline-none focus:ring-1 focus:ring-primary-500"
              />
              <button
                onClick={ask}
                disabled={!input.trim() || loading}
                className="self-end px-3 py-2 rounded-lg bg-primary-600 hover:bg-primary-500 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-medium transition-colors min-w-[48px] flex items-center justify-center"
              >
                {loading
                  ? <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                    </svg>
                  : 'Ask'}
              </button>
            </div>

            {(answer || error) && (
              <div className="rounded-lg border border-primary-500/20 bg-primary-500/5 p-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs text-primary-400 font-medium">Answer</span>
                  <button onClick={clear} className="text-gray-600 hover:text-gray-400 transition-colors" title="Clear">
                    <XMarkIcon className="w-3.5 h-3.5" />
                  </button>
                </div>
                {error ? (
                  <p className="text-xs text-red-400">{error}</p>
                ) : (
                  <div className="max-h-64 overflow-y-auto pr-1 prose prose-invert prose-sm max-w-none
                    prose-p:text-gray-300 prose-p:leading-relaxed prose-p:my-1
                    prose-strong:text-white
                    prose-ul:text-gray-300 prose-ul:my-1 prose-li:my-0
                    prose-ol:text-gray-300">
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>{answer ?? ''}</ReactMarkdown>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default HelpWidget;
